import {
  DEFAULT_INFERENCE_MODEL,
  IInferenceModel,
  INFERENCE_MAX_NEW_TOKENS_KEY,
  INFERENCE_MODEL_KEY,
  INFERENCE_TEMPERATURE_KEY,
  INFERENCE_TOPK_KEY,
  INFERENCE_TOPP_KEY,
  RootState,
  setInferenceMaxNewTokens,
  setInferenceModel,
  setInferenceTemperature,
  setInferenceTopK,
  setInferenceTopP,
  useApp,
  useAppDispatch,
  useAppSelector,
} from "@next-platform/client-common-core";
import { useCallback, useEffect } from "react";

import { typedLocalStorageService } from "@/utils/localStorageRe";

export function useInferenceSettings() {
  const { inferenceModel, inferenceMaxNewTokens, inferenceTemperature, inferenceTopP, inferenceTopK } =
    useAppSelector((state: RootState) => state.settings) || {};
  const dispatch = useAppDispatch();
  const { inferenceModels } = useApp();
  const inferenceModelData = inferenceModels?.find((model: IInferenceModel) => model.id === inferenceModel) as IInferenceModel;

  const updateInferenceModel = useCallback(
    (model: string) => {
      dispatch(setInferenceModel(model));
    },
    [dispatch],
  );

  const updateInferenceMaxNewTokens = useCallback(
    (maxNewTokens: number) => {
      dispatch(setInferenceMaxNewTokens(maxNewTokens));
    },
    [dispatch],
  );
  const updateInferenceTemperature = useCallback(
    (temperature: number) => {
      dispatch(setInferenceTemperature(temperature));
    },
    [dispatch],
  );
  const updateInferenceTopP = useCallback(
    (topP: number) => {
      dispatch(setInferenceTopP(topP));
    },
    [dispatch],
  );
  const updateInferenceTopK = useCallback(
    (topK: number) => {
      dispatch(setInferenceTopK(topK));
    },
    [dispatch],
  );

  const applyInferenceSettings = (settings: { modelId?: string; maxNewTokens?: number; temperature?: number; topP?: number; topK?: number }) => {
    if (settings.modelId !== undefined) {
      updateInferenceModel(settings.modelId);
    }
    if (settings.maxNewTokens !== undefined) {
      updateInferenceMaxNewTokens(settings.maxNewTokens);
    }
    if (settings.temperature !== undefined) {
      updateInferenceTemperature(settings.temperature);
    }
    if (settings.topP !== undefined) {
      updateInferenceTopP(settings.topP);
    }
    if (settings.topK !== undefined) {
      updateInferenceTopK(settings.topK);
    }
  };

  const hydrateInferenceSettings = () => {
    updateInferenceModel(typedLocalStorageService.getItem(INFERENCE_MODEL_KEY) as unknown as string);
    updateInferenceMaxNewTokens(typedLocalStorageService.getItem(INFERENCE_MAX_NEW_TOKENS_KEY) as unknown as number);
    updateInferenceTemperature(typedLocalStorageService.getItem(INFERENCE_TEMPERATURE_KEY) as unknown as number);
    updateInferenceTopP(typedLocalStorageService.getItem(INFERENCE_TOPP_KEY) as unknown as number);
    updateInferenceTopK(typedLocalStorageService.getItem(INFERENCE_TOPK_KEY) as unknown as number);
  };

  useEffect(() => {
    if (inferenceModels?.length > 0) {
      //if a model no longer exists, we set back to default
      if (!inferenceModels?.find((model: IInferenceModel) => model?.id === inferenceModel)) {
        updateInferenceModel(DEFAULT_INFERENCE_MODEL);
      }
    }
  }, [inferenceModels, inferenceModel, updateInferenceModel]);

  return {
    inferenceModel,
    updateInferenceModel,
    inferenceMaxNewTokens,
    updateInferenceMaxNewTokens,
    inferenceTemperature,
    updateInferenceTemperature,
    inferenceTopP,
    updateInferenceTopP,
    inferenceTopK,
    updateInferenceTopK,
    applyInferenceSettings,
    hydrateInferenceSettings,
    inferenceModelData,
  };
}
